import React, { useRef } from "react";
import { Dialog, DialogTitle, DialogContent, Box, IconButton, Typography, Button } from "@mui/material";
import CloseIcon from "@mui/icons-material/Close";
import PrintOutlinedIcon from "@mui/icons-material/PrintOutlined";
import { QRCodeSVG } from "qrcode.react";
import { labelStyles } from "../../styles/formStyles";

interface QRDisplayProps {
  open: boolean;
  onClose: () => void;
  value: string;
  label?: string;
  size?: number;
  showPrint?: boolean;
  showPrintText?: boolean;
  showLabel?: boolean;
  labelStyle?: object;
  showRequired?: boolean;
}

const QRDisplay: React.FC<QRDisplayProps> = ({
  open,
  onClose,
  value,
  label = "QR Code",
  size = 200,
  showPrint = true,
  showPrintText = true,
  showLabel = true,
  labelStyle,
  showRequired = false,
}) => {
  const qrRef = useRef<HTMLDivElement>(null);
  
  // Generate the full tracking URL for QR code (for external scanning)
  const trackingUrl = `https://app.logistics.trackifyassets.com/track/${value}`;

  const handlePrint = () => {
    if (!qrRef.current) return;
    const svgEl = qrRef.current.querySelector("svg");
    if (!svgEl) return;
    const svgData = new XMLSerializer().serializeToString(svgEl);
    const printWindow = window.open("", "_blank");
    if (printWindow) {
      printWindow.document.write(`
        <html>
          <head><title>Print QR Code</title>
            <style>
              body { margin: 0; padding: 20px; display: flex; flex-direction: column; align-items: center; justify-content: center; min-height: 100vh; font-family: Arial, sans-serif; }
              svg { max-width: 100%; height: auto; }
              p { margin-top: 10px; font-size: 14px; color: #333; }
            </style>
          </head>
          <body>${svgData}<p>${value}</p></body>
        </html>
      `);
      printWindow.document.close();
      printWindow.print();
    }
  };

  if (!value) return null;

  return (
    <Dialog open={open} onClose={onClose} PaperProps={{ sx: { borderRadius: "12px", minWidth:"30%" } }}>
      <DialogTitle sx={{ display: "flex", justifyContent: "space-between", alignItems: "center", pb: 0 }}>
        <Typography fontWeight={600}>Package QR Code</Typography>
        <IconButton onClick={onClose} size="small"><CloseIcon /></IconButton>
      </DialogTitle>
      <DialogContent sx={{ pt: 2 }}>
        <Box>
          {showLabel && (
            <Box sx={{ display: "flex", justifyContent: "space-between", alignItems: "center" }}>
              <Typography component="label" sx={labelStyle || labelStyles}>
                {label}
                {showRequired && <span style={{ color: "red" }}>*</span>}
              </Typography>
              {showPrint && (
                showPrintText ? (
                  <Button
                    onClick={handlePrint}
                    startIcon={<PrintOutlinedIcon sx={{ fontSize: "18px" }} />}
                    sx={{ color: "primary.main", textTransform: "none", fontSize: "14px", padding: "4px 8px", minWidth: "auto" }}
                  >
                    Print
                  </Button>
                ) : (
                  <IconButton onClick={handlePrint} sx={{ color: "primary.main", padding: "4px" }}>
                    <PrintOutlinedIcon sx={{ fontSize: "18px" }} />
                  </IconButton>
                )
              )}
            </Box>
          )}
          <Box
            ref={qrRef}
            sx={{
              border: "1px solid #e0e0e0",
              borderRadius: "8px",
              padding: "16px",
              backgroundColor: "#fafafa",
              display: "flex",
              flexDirection: "column",
              alignItems: "center",
              gap: 1,
            }}
          >
            <QRCodeSVG value={trackingUrl} size={size} />
            <Typography variant="body2" color="text.secondary" sx={{ fontSize: "12px" }}>{value}</Typography>
          </Box>
        </Box>
      </DialogContent>
    </Dialog>
  );
};

export default QRDisplay;
